import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import {
  FormaButton,
  FormaButtonGroup,
  FormaFormField,
  FormaIconButton,
  FormaInput,
  FormaLabel,
} from 'forma';
import { DialogType } from './dialog.type';

interface ConfirmationDialogData {
  title: string | { key: string; value: string };
  body: string;
  trueOption: string;
  falseOption: string;
  type: DialogType;
  translate: boolean;
  label: string;
  value: string;
  placeholder: string;
  translationParameter: string;
  description: string;
}

/**
 * Legacy confirmation / info / input dialog content. Kept as an adapter over
 * FormaDialogContainer so all existing ConfirmationDialogService callers get
 * the new Forma look
 *
 * Its layout mirrors FormaDialogContent (heading + body + actions row) but
 * adds:
 *   - transloco translation of title / body / button labels
 *   - innerHTML rendering of the body (some callers embed anchor tags)
 *   - support for a `{ key, value }` title with transloco parameters
 *   - an input field for DialogType.Input
 */
@Component({
  selector: 'app-confirmation-dialog',
  templateUrl: './confirmation-dialog.component.html',
  styleUrls: ['./confirmation-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    TranslocoModule,
    FormaButton,
    FormaButtonGroup,
    FormaIconButton,
    FormaFormField,
    FormaInput,
    FormaLabel,
  ],
})
export class ConfirmationDialogComponent {
  dialogRef = inject<DialogRef<boolean | string, ConfirmationDialogComponent>>(DialogRef);

  data = inject<ConfirmationDialogData>(DIALOG_DATA);

  DialogType = DialogType;

  isTitleString(): boolean {
    return typeof this.data.title === 'string';
  }

  getTitleString(): string {
    return this.data.title as string;
  }

  getTitleObject(): { key: string; value: string } {
    return this.data.title as { key: string; value: string };
  }

  onPrimary(): void {
    if (this.data.type === DialogType.Input) {
      this.dialogRef.close(this.data.value ? this.data.value : (this.data.placeholder));
      return;
    }
    this.dialogRef.close(true);
  }

  onSecondary(): void {
    this.dialogRef.close(false);
  }
}
